import React, { useEffect, useState } from 'react';
import s from '../DietPlanBuilder.module.css';
import { copy } from '../../copy';
import { fmtDate, fmtG, fmtInt } from '../../lib/format';
import { gramsPerDay } from '../../lib/nutrition';
import type { Builder, Dispatch } from '../../hooks/useDietPlanBuilder';
import type { MealStat } from '../../hooks/useDietPlanBuilder';
import type { OccupiedRange } from '../../lib/planDates';
import type { Plan, SaveTplMode, Targets } from '../../types';

const fmtMealTarget = (T: Targets) =>
  `${fmtInt(T.k)} kcal · P ${fmtG(T.p)} · C ${fmtG(T.c)} · F ${fmtG(T.f)}`;

const offOptionIndices = (stat: MealStat) =>
  stat.opts.map((o, i) => (o.st !== 'ok' ? i + 1 : null)).filter((n): n is number => n != null);

export function PreviewAssignSheet({
  plan,
  derived,
  state,
  dispatch,
  onAssign,
  onClose,
  overlap,
  suggestedRange,
  onUseFreeDates,
}: {
  plan: Plan;
  derived: NonNullable<Builder['derived']>;
  state: Builder['s'];
  dispatch: Dispatch;
  onAssign: () => Promise<boolean>;
  onClose: () => void;
  overlap: OccupiedRange | null;
  suggestedRange: { from: string; to: string };
  onUseFreeDates: () => void;
}) {
  const { active, offOpts, blocked, issues, shareSum, tipsOverLimit } = derived;
  const gp = gramsPerDay(plan.kcal, plan.pct, 'p');
  const gc = gramsPerDay(plan.kcal, plan.pct, 'c');
  const gf = gramsPerDay(plan.kcal, plan.pct, 'f');
  const hasOffTargets = offOpts > 0;
  const showWarnings = blocked || hasOffTargets || shareSum !== 100;
  const needsConfirm = showWarnings && !overlap;
  const copyNameMissing = state.saveTplMode === 'copy' && !state.newTplName.trim();
  const assignDisabled = !!overlap || (needsConfirm && !state.confirm) || copyNameMissing;
  const [busy, setBusy] = useState(false);

  const patch = (p: { confirm?: boolean; saveTplMode?: SaveTplMode; newTplName?: string }) =>
    dispatch({ type: 'assignField', patch: p });

  const reviewConfirm = (
    <label className={s.previewAssignConfirm}>
      <input
        type="checkbox"
        checked={state.confirm}
        onChange={e => patch({ confirm: e.target.checked })}
      />
      {copy.assign.confirm}
    </label>
  );

  const setSaveMode = (mode: SaveTplMode) => {
    if (mode === 'copy' && !state.newTplName.trim()) {
      const base = plan.templateName || state.tplName || plan.name;
      patch({ saveTplMode: mode, newTplName: base ? `${base} (copy)` : '' });
      return;
    }
    patch({ saveTplMode: mode });
  };

  const offByMeal = active
    .map(stat => ({ name: stat.m.name, nums: offOptionIndices(stat) }))
    .filter(row => row.nums.length > 0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const masterTplName = plan.templateName || state.tplName;
  const canUpdateMaster = !!plan.templateId && !!masterTplName;

  return (
    <div className={s.previewAssignScrim} role="dialog" aria-modal aria-label={copy.preview.sheetTitle}>
      <div className={s.scrimBg} onClick={onClose} />
      <div className={s.previewAssignDialog} onClick={e => e.stopPropagation()}>
        <div>
          <h4 className={s.previewAssignTitle}>{plan.name}</h4>
          <div className={s.previewAssignSub}>
            {fmtDate(plan.from)} – {fmtDate(plan.to)} · {fmtInt(plan.kcal)} kcal a day · P {gp} g · C {gc} g · F {gf} g
          </div>
        </div>

        <div className={s.previewAssignGrid}>
          <span className={s.previewAssignHead}>Meal</span>
          <span className={s.previewAssignHead}>Target</span>
          <span className={s.previewAssignHead}>Options</span>
          <span className={s.previewAssignHead} data-last>Check</span>
          {active.map(stat => {
            const bad = stat.opts.filter(o => o.st !== 'ok').length;
            return (
              <React.Fragment key={stat.m.key}>
                <span className={s.previewAssignCell}>
                  <b className={s.previewAssignMealName}>{stat.m.name}</b>
                  <br />
                  <span className={s.previewAssignMealTime}>{stat.m.time}</span>
                </span>
                <span className={s.previewAssignCell} data-tabular>
                  {fmtMealTarget(stat.T)}
                </span>
                <span className={s.previewAssignCell} data-tabular>
                  {stat.opts.length}
                </span>
                <span
                  className={s.previewAssignCell}
                  data-check={bad ? 'bad' : 'ok'}
                >
                  {bad ? copy.meals.optionOff(bad) : copy.meals.onTargetStatus}
                </span>
              </React.Fragment>
            );
          })}
        </div>

        {overlap && (
          <div className={s.previewAssignAlert} data-blocked role="alert">
            <span className={s.previewAssignAlertIcon} aria-hidden>!</span>
            <div className={s.previewAssignAlertBody}>
              <b>{copy.goals.overlap(overlap.name, fmtDate(overlap.from), fmtDate(overlap.to))}</b>
              <button type="button" className={s.overlapFixBtn} onClick={onUseFreeDates}>
                {copy.goals.useFreeDates(fmtDate(suggestedRange.from), fmtDate(suggestedRange.to))}
              </button>
            </div>
          </div>
        )}
        {showWarnings && (
          <div className={s.previewAssignAlert} data-blocked={blocked || undefined}>
            <span className={s.previewAssignAlertIcon} aria-hidden>!</span>
            <div className={s.previewAssignAlertBody}>
              {blocked ? (
                <>
                  <b>{tipsOverLimit ? copy.meals.tipsOverLimitFooter : copy.assign.fix}</b>
                  {shareSum !== 100 && (
                    <span>{copy.assign.splitIssue(shareSum)}</span>
                  )}
                  {issues.map(i => (
                    <span key={i.name}>
                      <b>{i.name}:</b> {copy.assign.mealIssue(i.bad, i.all, i.empty)}
                    </span>
                  ))}
                </>
              ) : (
                <>
                  <b>{copy.preview.farFrom(offOpts)}</b>
                  {offByMeal.map(row => (
                    <span key={row.name}>
                      <b>{row.name}:</b> {copy.preview.optionNums(row.nums)}
                    </span>
                  ))}
                </>
              )}
              <span>{copy.assign.offNote}</span>
              {reviewConfirm}
            </div>
          </div>
        )}

        <fieldset className={s.previewAssignTplSave}>
          <legend className={s.previewAssignTplLegend}>{copy.preview.saveTplSection}</legend>
          <label className={s.previewAssignTplChoice}>
            <input
              type="radio"
              name="saveTplMode"
              checked={state.saveTplMode === 'none'}
              onChange={() => setSaveMode('none')}
            />
            <span>{copy.preview.saveTplNone}</span>
          </label>
          {canUpdateMaster && (
            <label className={s.previewAssignTplChoice}>
              <input
                type="radio"
                name="saveTplMode"
                checked={state.saveTplMode === 'master'}
                onChange={() => setSaveMode('master')}
              />
              <span>
                <b>{copy.preview.saveMasterChanges}</b>
                <span>{copy.preview.saveMasterChangesNote(masterTplName)}</span>
              </span>
            </label>
          )}
          <label className={s.previewAssignTplChoice}>
            <input
              type="radio"
              name="saveTplMode"
              checked={state.saveTplMode === 'copy'}
              onChange={() => setSaveMode('copy')}
            />
            <span>
              <b>{copy.preview.saveTplCopy}</b>
              <span>{copy.preview.saveTplCopyNote}</span>
            </span>
          </label>
          {state.saveTplMode === 'copy' && (
            <label className={s.previewAssignTplName}>
              <span className={s.previewAssignTplNameLabel}>{copy.preview.saveTplCopyName}</span>
              <input
                type="text"
                className={s.input}
                value={state.newTplName}
                onChange={e => patch({ newTplName: e.target.value })}
                placeholder={copy.preview.saveTplCopyPh}
              />
            </label>
          )}
        </fieldset>

        <div className={s.previewAssignActions}>
          <button type="button" className={s.btnOutlineGray} onClick={onClose}>
            {copy.preview.backToEdit}
          </button>
          <button
            type="button"
            className={s.btnSolidGreen}
            disabled={assignDisabled || busy}
            onClick={async () => {
              setBusy(true);
              try {
                const ok = await onAssign();
                if (ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? '…' : needsConfirm ? copy.assign.anyway : copy.assign.button}
          </button>
        </div>
      </div>
    </div>
  );
}
